import type { components } from "@sagot/api-client";
import type { IconName } from "@sagot/ui";

export type Announcement = components["schemas"]["AnnouncementOut"];
export type AnnouncementView = components["schemas"]["AnnouncementView"];
export type AnnouncementChannel = components["schemas"]["AnnouncementChannel"];
export type AnnouncementCreate = components["schemas"]["AnnouncementCreate"];
export type AnnouncementUpdate = components["schemas"]["AnnouncementUpdate"];

export const VIEWS: readonly AnnouncementView[] = ["all", "sent", "scheduled", "draft", "archived"];
export const CHANNELS: readonly AnnouncementChannel[] = ["push", "webapp_banner", "widget_pin", "email"];
export const CHANNEL_ICONS: Readonly<Record<AnnouncementChannel, IconName>> = {
  push: "smartphone",
  webapp_banner: "app-window",
  widget_pin: "message-square",
  email: "mail",
};
export const PUSH_PREVIEW_CHARS = 120;
export const TITLE_MAX = 65;
export const BODY_MAX = 500;
/** The public audience code (RAG spec §12): everyone. */
export const PUBLIC_AUDIENCE = 1;

/** Legacy channel names still present in seeded rows (API gap: should be normalized server-side). */
const CHANNEL_ALIASES: Readonly<Record<string, AnnouncementChannel>> = {
  mobile_push: "push",
  widget_pinned: "widget_pin",
  webapp: "webapp_banner",
  widget: "widget_pin",
};

function isChannel(value: string): value is AnnouncementChannel {
  return (CHANNELS as readonly string[]).includes(value);
}

/** Maps API channel strings to the request enum, dropping unknown values and duplicates. */
export function normalizeChannels(values: readonly string[]): AnnouncementChannel[] {
  const mapped = values.map((v) => (isChannel(v) ? v : CHANNEL_ALIASES[v])).filter((v): v is AnnouncementChannel => v !== undefined);
  return CHANNELS.filter((c) => mapped.includes(c));
}

export type DisplayStatus = "sent" | "sending" | "scheduled" | "draft" | "archived";

export function displayStatus(status: string): DisplayStatus {
  if (status === "sending" || status === "scheduled" || status === "draft" || status === "archived") return status;
  if (status === "expired") return "archived";
  return "sent";
}

/** Drafts and scheduled announcements are editable (API `EDITABLE_STATUSES`). */
export function isEditable(status: DisplayStatus): boolean {
  return status === "draft" || status === "scheduled";
}

/** Segment options = every segment already used by the workspace's announcements, sorted. */
export function knownSegments(items: readonly Announcement[], extra: readonly string[] = []): string[] {
  return [...new Set([...items.flatMap((a) => a.segments), ...extra])].sort((a, b) => a.localeCompare(b));
}
